// Plating [Emplatado] (M16): built-in plating and shoot guide cards plus the restaurant's own. Pick a card,
// choose the language (Spanish, English or both), save PNG / PDF for the kitchen. "Duplicate to edit" makes
// an own card: rename it after a menu dish, drag the elements on the drawing, relabel them, add a reference
// photo, edit the tips.
import { useEffect, useRef, useState } from 'react';
import { useLang, useT, type MessageKey } from '../../i18n';
import {
  BUILT_IN_CARDS,
  KIND_TEXT,
  PLATE_TEXT,
  ANGLE_TEXT,
  type PlatingCard,
  type PlateElement,
  type ElementKind,
  type ElementShape,
  type PlateKind,
  type CameraAngle,
} from '../../features/plating/cards';
import { CARD_H, CARD_W, drawCard, type CardLang } from '../../features/plating/drawCard';
import {
  cardById,
  cardFile,
  cardPdf,
  cardPng,
  deleteCard,
  duplicateCard,
  importCardFile,
  loadCards,
  updateCard,
  usePlating,
} from '../../features/plating/store';
import { useLibrary } from '../../state/library';
import { useEditor } from '../../state/editor';
import { exportWithExtras } from '../../features/exportHelpers';
import { sanitizeName } from '../../io/rename';
import s from '../../design/ui/ad/Ad.module.css';

function saveBytes(bytes: Uint8Array, name: string, type: string): Promise<string> | string {
  const folder = useLibrary.getState().folder;
  if (folder?.mode === 'fsa') return folder.writeExport('plating', name, bytes);
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

/** Live preview; on own cards, elements can be dragged. */
function Preview({ card, lang }: { card: PlatingCard; lang: CardLang }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [photo, setPhoto] = useState<ImageBitmap | null>(null);
  const drag = useRef<{ id: string; dx: number; dy: number } | null>(null);
  useEffect(() => {
    let alive = true;
    if (!card.photo) return setPhoto(null);
    void createImageBitmap(
      new Blob([card.photo.bytes as Uint8Array<ArrayBuffer>], { type: card.photo.type }),
    ).then((b) => alive && setPhoto(b));
    return () => {
      alive = false;
    };
  }, [card.photo]);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    drawCard(c.getContext('2d')!, card, lang, c.width, c.height, photo);
  }, [card, lang, photo]);
  // Diagram area in card units: (50, 180) size 720.
  const toDiagram = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    const cx = ((e.clientX - r.left) / r.width) * CARD_W,
      cy = ((e.clientY - r.top) / r.height) * CARD_H;
    return { x: (cx - 50) / 720, y: (cy - 180) / 720 };
  };
  return (
    <canvas
      ref={ref}
      width={CARD_W}
      height={CARD_H}
      data-testid="plating-preview"
      style={{
        height: '100%',
        maxWidth: '100%',
        width: 'auto',
        maxHeight: 'calc(100vh - 120px)',
        aspectRatio: `${CARD_W} / ${CARD_H}`,
        background: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,.4)',
        touchAction: 'none',
        cursor: card.builtIn ? 'default' : 'grab',
      }}
      onPointerDown={(e) => {
        if (card.builtIn) return;
        const p = toDiagram(e);
        const hit = [...card.elements]
          .reverse()
          .find((el) => Math.abs(el.x - p.x) < el.w / 2 + 0.02 && Math.abs(el.y - p.y) < el.h / 2 + 0.02);
        if (!hit) return;
        drag.current = { id: hit.id, dx: p.x - hit.x, dy: p.y - hit.y };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const p = toDiagram(e);
        void updateCard(card.id, (c) => {
          const el = c.elements.find((x) => x.id === d.id);
          if (el) {
            el.x = Math.min(1, Math.max(0, p.x - d.dx));
            el.y = Math.min(1, Math.max(0, p.y - d.dy));
          }
        });
      }}
      onPointerUp={() => (drag.current = null)}
    />
  );
}

const SHAPES: ElementShape[] = ['circle', 'ellipse', 'rect', 'leaf', 'drop', 'triangle', 'stack'];

function Editor({ card }: { card: PlatingCard }) {
  const t = useT();
  const lang = useLang((l) => l.lang);
  const upd = (fn: (c: PlatingCard) => void) => void updateCard(card.id, fn);
  const active = useLibrary((l) => l.photos.find((p) => p.id === l.activeId) ?? null);
  const setPhotoFromLibrary = async () => {
    if (!active) return;
    const res = await exportWithExtras(
      active,
      useEditor.getState().get(active.id),
      {
        format: 'jpeg',
        bitDepth: 8,
        quality: 85,
        size: { mode: 'longEdge', w: 1000 },
        dpi: 72,
        colorSpace: 'srgb',
        sharpen: { target: 'none', amount: 'standard' },
        background: 'asEdited',
        metadata: 'stripAll',
      },
      {},
    );
    upd((c) => void (c.photo = { bytes: res.bytes, type: 'image/jpeg' }));
  };
  const elField = (el: PlateElement, fn: (e: PlateElement) => void) =>
    upd((c) => fn(c.elements.find((x) => x.id === el.id)!));
  return (
    <div data-testid="plating-editor">
      <h4>{t('platingCardName')}</h4>
      <div className={s.pair}>
        <label className={s.field}>
          <span>{t('adSpanish')}</span>
          <input
            type="text"
            data-testid="plating-name-es"
            value={card.name.es}
            onChange={(e) => upd((c) => void (c.name.es = e.target.value))}
          />
        </label>
        <label className={s.field}>
          <span>{t('adEnglish')}</span>
          <input
            type="text"
            data-testid="plating-name-en"
            value={card.name.en}
            onChange={(e) => upd((c) => void (c.name.en = e.target.value))}
          />
        </label>
      </div>
      <div className={s.pair}>
        <label className={s.field}>
          <span>{t('platingPlate')}</span>
          <select
            value={card.plate}
            onChange={(e) => upd((c) => void (c.plate = e.target.value as PlateKind))}
          >
            {(Object.keys(PLATE_TEXT) as PlateKind[]).map((p) => (
              <option key={p} value={p}>
                {PLATE_TEXT[p][lang]}
              </option>
            ))}
          </select>
        </label>
        <label className={s.field}>
          <span>{t('platingAngle')}</span>
          <select
            value={card.angle}
            onChange={(e) => upd((c) => void (c.angle = e.target.value as CameraAngle))}
          >
            {(Object.keys(ANGLE_TEXT) as CameraAngle[]).map((a) => (
              <option key={a} value={a}>
                {ANGLE_TEXT[a][lang]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <h4>{t('platingElements')}</h4>
      <p className={s.small}>{t('platingDragHint')}</p>
      {card.elements.map((el, i) => (
        <div key={el.id} style={{ borderBottom: '1px solid var(--line)', paddingBottom: 6, marginBottom: 6 }}>
          <div className={s.pair}>
            <label className={s.field}>
              <span>
                {i + 1}. {t('adSpanish')}
              </span>
              <input
                type="text"
                data-testid={`plating-el-${i}-es`}
                value={el.label.es}
                onChange={(e) => elField(el, (x) => void (x.label.es = e.target.value))}
              />
            </label>
            <label className={s.field}>
              <span>{t('adEnglish')}</span>
              <input
                type="text"
                value={el.label.en}
                onChange={(e) => elField(el, (x) => void (x.label.en = e.target.value))}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select
              value={el.kind}
              onChange={(e) => elField(el, (x) => void (x.kind = e.target.value as ElementKind))}
            >
              {(Object.keys(KIND_TEXT) as ElementKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_TEXT[k][lang]}
                </option>
              ))}
            </select>
            <select
              value={el.shape}
              onChange={(e) => elField(el, (x) => void (x.shape = e.target.value as ElementShape))}
            >
              {SHAPES.map((sh) => (
                <option key={sh}>{sh}</option>
              ))}
            </select>
            <input
              type="color"
              value={el.color}
              onChange={(e) => elField(el, (x) => void (x.color = e.target.value))}
            />
            <button
              onClick={() => upd((c) => void (c.elements = c.elements.filter((x) => x.id !== el.id)))}
              aria-label={t('platingRemove')}
            >
              ✕
            </button>
          </div>
          <label className={s.field}>
            <span>{t('adSize')}</span>
            <input
              type="range"
              min={0.03}
              max={0.7}
              step={0.01}
              value={el.w}
              onChange={(e) => {
                const v = Number(e.target.value);
                elField(el, (x) => {
                  x.h = (x.h / x.w) * v;
                  x.w = v;
                });
              }}
            />
          </label>
          <label className={s.field}>
            <span>{t('platingRotate')}</span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={el.rot}
              onChange={(e) => elField(el, (x) => void (x.rot = Number(e.target.value)))}
            />
          </label>
        </div>
      ))}
      <button
        data-testid="plating-add-element"
        onClick={() =>
          upd(
            (c) =>
              void c.elements.push({
                id: `e${Date.now()}`,
                kind: 'garnish',
                shape: 'circle',
                x: 0.5,
                y: 0.5,
                w: 0.1,
                h: 0.1,
                rot: 0,
                color: '#4f9a3a',
                label: { en: 'New item', es: 'Elemento nuevo' },
              }),
          )
        }
      >
        + {t('platingAddElement')}
      </button>
      <h4>{t('platingLight')}</h4>
      <label className={s.field}>
        <span>{t('adSpanish')}</span>
        <textarea
          rows={2}
          value={card.light.es}
          onChange={(e) => upd((c) => void (c.light.es = e.target.value))}
        />
      </label>
      <label className={s.field}>
        <span>{t('adEnglish')}</span>
        <textarea
          rows={2}
          value={card.light.en}
          onChange={(e) => upd((c) => void (c.light.en = e.target.value))}
        />
      </label>
      <h4>{t('platingSteps')}</h4>
      <p className={s.small}>{t('platingStepsHint')}</p>
      <label className={s.field}>
        <span>{t('adSpanish')}</span>
        <textarea
          rows={4}
          data-testid="plating-steps-es"
          value={card.steps.map((x) => x.es).join('\n')}
          onChange={(e) =>
            upd((c) => {
              const es = e.target.value.split('\n');
              c.steps = es.map((v, i) => ({ es: v, en: c.steps[i]?.en ?? '' }));
            })
          }
        />
      </label>
      <label className={s.field}>
        <span>{t('adEnglish')}</span>
        <textarea
          rows={4}
          value={card.steps.map((x) => x.en).join('\n')}
          onChange={(e) =>
            upd((c) => {
              const en = e.target.value.split('\n');
              c.steps = c.steps.map((v, i) => ({ ...v, en: en[i] ?? '' }));
            })
          }
        />
      </label>
      <h4>{t('platingPhoto')}</h4>
      {active && (
        <button data-testid="plating-photo-current" onClick={() => void setPhotoFromLibrary()}>
          📷 {t('adUseCurrentPhoto')}
        </button>
      )}{' '}
      <label>
        <input
          type="file"
          accept="image/png,image/jpeg"
          data-testid="plating-photo-file"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (f) {
              const bytes = new Uint8Array(await f.arrayBuffer());
              upd((c) => void (c.photo = { bytes, type: f.type || 'image/jpeg' }));
            }
          }}
        />
      </label>
      {card.photo && (
        <button onClick={() => upd((c) => void (c.photo = null))}>{t('platingPhotoRemove')}</button>
      )}
    </div>
  );
}

export function PlatingWorkspace() {
  const t = useT();
  const ui = useLang((l) => l.lang);
  const mine = usePlating((p) => p.mine);
  const selected = usePlating((p) => p.selected);
  const lang = usePlating((p) => p.lang);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => void loadCards(), []);
  const card = cardById(selected) ?? BUILT_IN_CARDS[0]!;
  const base = sanitizeName(card.name[lang === 'en' ? 'en' : 'es']) || 'card';
  const run = async (fn: () => Promise<string> | string) => {
    setMsg(null);
    try {
      setMsg(t('saved', { name: await fn() }));
    } catch (e) {
      setMsg(t('saveFailed', { reason: (e as Error).message }));
    }
  };
  const list = (cards: PlatingCard[], title: MessageKey) => (
    <>
      <h4>{t(title)}</h4>
      {cards.length === 0 && <p className={s.small}>{t('platingNoneYet')}</p>}
      {cards.map((c) => (
        <button
          key={c.id}
          data-testid={`plating-card-${c.id}`}
          aria-pressed={c.id === card.id}
          onClick={() => usePlating.setState({ selected: c.id })}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            marginBottom: 3,
            ...(c.id === card.id ? { background: 'var(--accent)', color: '#fff' } : {}),
          }}
        >
          {c.name[ui]}
        </button>
      ))}
    </>
  );
  return (
    <div
      style={{
        gridArea: '2 / 1 / 4 / 4',
        display: 'grid',
        gridTemplateColumns: '240px 1fr 360px',
        gridTemplateRows: 'minmax(0, 1fr)',
        minHeight: 0,
      }}
      data-testid="plating-workspace"
    >
      <aside
        className={s.form}
        style={{ gridArea: 'auto', borderLeft: 'none', borderRight: '1px solid var(--line)' }}
      >
        <h3 style={{ margin: '4px 0' }}>{t('platingTitle')}</h3>
        <p className={s.small}>{t('platingIntro')}</p>
        {list(mine, 'platingMine')}
        {list(BUILT_IN_CARDS, 'platingBuiltIn')}
      </aside>
      <main
        style={{
          display: 'grid',
          placeItems: 'center',
          padding: 16,
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          background: '#3a3a3a',
        }}
      >
        <Preview card={card} lang={lang} />
      </main>
      <aside className={s.form} style={{ gridArea: 'auto' }}>
        <label className={s.field}>
          <span>{t('platingLanguage')}</span>
          <select
            data-testid="plating-lang"
            value={lang}
            onChange={(e) => usePlating.setState({ lang: e.target.value as CardLang })}
          >
            <option value="es">Español</option>
            <option value="en">English</option>
            <option value="both">{t('platingBoth')}</option>
          </select>
        </label>
        <button
          className={`${s.big} ${s.primary}`}
          data-testid="plating-save-png"
          onClick={() =>
            void run(async () => saveBytes(await cardPng(card, lang), `${base}_${lang}.png`, 'image/png'))
          }
        >
          💾 {t('adSavePng')}
        </button>
        <button
          className={s.big}
          data-testid="plating-save-pdf"
          onClick={() =>
            void run(async () =>
              saveBytes(await cardPdf(card, lang), `${base}_${lang}.pdf`, 'application/pdf'),
            )
          }
        >
          📄 {t('platingSavePdf')}
        </button>
        {card.builtIn ? (
          <button
            className={s.big}
            data-testid="plating-duplicate"
            onClick={() => void duplicateCard(card.id)}
          >
            ✎ {t('platingDuplicate')}
          </button>
        ) : (
          <>
            <button
              className={s.big}
              data-testid="plating-duplicate"
              onClick={() => void duplicateCard(card.id)}
            >
              ⧉ {t('platingCopy')}
            </button>
            <button
              className={s.big}
              onClick={() =>
                void run(() =>
                  saveBytes(
                    new TextEncoder().encode(cardFile(card)),
                    `${base}.epbplate.json`,
                    'application/json',
                  ),
                )
              }
            >
              {t('platingExportFile')}
            </button>
            <button className={s.big} data-testid="plating-delete" onClick={() => void deleteCard(card.id)}>
              🗑 {t('platingDelete')}
            </button>
          </>
        )}
        <label className={s.field} style={{ marginTop: 8 }}>
          <span>{t('platingImportFile')}</span>
          <input
            type="file"
            accept=".json,application/json"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                usePlating.setState({ selected: await importCardFile(await f.text()) });
              } catch (err) {
                setMsg((err as Error).message);
              }
            }}
          />
        </label>
        {msg && (
          <div role="status" className={s.status} data-testid="plating-status">
            {msg}
          </div>
        )}
        {!card.builtIn && <Editor card={card} />}
      </aside>
    </div>
  );
}
